import React, { useEffect, useRef } from 'react';
import { App, Flex, Skeleton, theme } from 'antd';
import { useNavigate } from 'react-router-dom';

import { NoCrawlersSelected } from '~features/empty-states';
import { useNoCrawlersSelected } from '~features/crawler-filter/CrawlerFilterHooks';
import { IsPrintingProvider, openExportWindow } from '~library/print';

import { BlurringOverlay } from '../../../components/BlurringOverlay/BlurringOverlay';
import { usePrerenderUserId } from '../../../hooks/usePrerenderUser';
import CrawlerActivityChartsSection from './components/CrawlerActivityChartsSection';
import DiscoverabilityFilters from './components/DiscoverabilityFilters';
import DiscoverabilityKPIs from './components/DiscoverabilityKPIs';
import DiscoverabilityNoActivityEmptyState from './components/DiscoverabilityNoActivityEmptyState';
import PagesToReviewSection from './components/PagesToReviewSection';
import PrintableDiscoverability from './components/PrintableDiscoverability';
import DiscoverabilityStatusBanner from './components/DiscoverabilityStatusBanner';
import { buildDiscoverabilityExportUrl } from './discoverabilityExportUrl';
import { useScanScopeParams } from './hooks/useScanScopeParams';
import { useDiscoverabilityCrawlerFilter } from './hooks/useDiscoverabilityCrawlerFilter';
import type { DiscoverabilityInspectPagesTab } from './DiscoverabilityAnalyticsEvents';
import { useDiscoverabilityEntitlement } from './hooks/useDiscoverabilityEntitlement';
import { useDiscoverabilitySearchParams } from './hooks/useDiscoverabilitySearchParams';
import { useDismissibleStatusBanner } from './hooks/useDismissibleStatusBanner';
import { useGetDiscoverabilityKpisQuery } from './api/discoverabilityKpisApi';
import { toSecondPrecisionUtc } from './api/discoverabilityPagesScope';
import {
  isEvaluableDomain,
  useGetDiscoverabilityStatusQuery,
} from './api/discoverabilityStatusApi';
import { USER_TIMEZONE } from './userTimezone';
import useTrackDiscoverabilityEvent from './hooks/useTrackDiscoverabilityEvent';

const INSPECT_PAGES_WINDOW_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Upper bound on the scan-scope `domains` list the Pages-to-Review endpoints
// accept. Mirrors rest-api `dto/request/scan-scope-domains.constants.ts`
// (`MAX_SCAN_SCOPE_DOMAINS`) — keep in sync. Beyond it we omit the param and
// let the table show all domains rather than send a list the API would 400.
export const MAX_SCAN_SCOPE_DOMAINS = 200;

// Formats an instant as a `YYYY-MM-DD` calendar date in the user's local
// timezone — the same shape `useDiscoverabilitySearchParams`'s `toIsoDate`
// produces and the date picker and day-granular KPIs/charts interpret. The
// absolute `dateTimeFrom`/`dateTimeTo` carriers stay UTC (below); only these
// plain calendar dates are localised.
function toLocalCalendarDate(instant: Date): string {
  const year = instant.getFullYear();
  const month = (instant.getMonth() + 1).toString().padStart(2, '0');
  const day = instant.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const NO_INTEGRATION_OVERLAY_CONTENT =
  'Complete your integration to see how AI Crawlers interact with your site.';

// Matches the main dashboard's menu item (DashboardV3Page).
const EXPORT_AS_PDF_LABEL = 'Export as PDF';

function DiscoverabilityScreen() {
  const { token } = theme.useToken();
  const { data, isLoading } = useGetDiscoverabilityStatusQuery();
  const prerenderUserId = usePrerenderUserId();
  const { isDismissed, dismiss } = useDismissibleStatusBanner(prerenderUserId);
  const trackEvent = useTrackDiscoverabilityEvent();
  const navigate = useNavigate();
  const entitlement = useDiscoverabilityEntitlement();
  const isNoIntegration = entitlement.kind === 'no-integration';
  const viewedFiredRef = useRef(false);
  const tabViewedFiredRef = useRef(false);
  const pagesToReviewRef = useRef<HTMLDivElement>(null);
  const scrollToPagesPending = useRef(false);

  const { message } = App.useApp();
  const { dateFrom, dateTo, crawlers, domain } = useDiscoverabilitySearchParams();
  const scanScope = useScanScopeParams();
  const crawlerSelection = useNoCrawlersSelected();
  const { noCrawlersSelected } = crawlerSelection;
  const { pickCrawlers } = useDiscoverabilityCrawlerFilter(crawlerSelection);
  const { data: kpisData, isLoading: isKpisLoading } = useGetDiscoverabilityKpisQuery(
    {
      dateFrom,
      dateTo,
      crawlers,
      domain,
      timezone: USER_TIMEZONE,
    },
    { skip: noCrawlersSelected }
  );

  // Tri-state on purpose: only a resolved `false` means "this account has never
  // been visited by an AI crawler". `undefined` (KPIs not loaded yet) must not
  // flash the warm-up placeholders for accounts that do have data.
  const waitingForCrawlerRequests = kpisData?.hasEverHadCrawlerActivity === false;
  const hasEverHadCrawlerActivity = kpisData?.hasEverHadCrawlerActivity === true;
  // Expired accounts with historical data get the same no-activity empty
  // state as active ones — "waiting for data" would be misleading and a
  // flat-zero chart reads as a site problem (PRE-3098).
  // With a crawler filter active, zero detections is the finding itself — let
  // DiscoverabilityKPIs render "0/N" + the Missing tag rather than the global
  // "no activity" empty state, whose headline is misleading while unselected
  // crawlers are active (PRE-3118 F1; mirrors the card's own hasCrawlerFilter).
  const hasCrawlerFilter = crawlers !== undefined && crawlers.length > 0;
  const hasNoActivityInRange =
    entitlement.kind !== 'no-integration' &&
    hasEverHadCrawlerActivity &&
    kpisData !== undefined &&
    kpisData.crawlersDetected.current === 0 &&
    !hasCrawlerFilter;

  // Until the KPIs land we can't tell whether the account has ever been
  // crawled, nor whether this range has activity, so render a skeleton instead
  // of the KPIs/Charts/Pages block — otherwise that block flashes before being
  // replaced by one of the two empty states. The daily-job status is not
  // consulted: it decides nothing below the banner (PRE-3305).
  const noActivityVerdictPending = entitlement.kind !== 'no-integration' && isKpisLoading;

  const scrollToPagesToReview = () => {
    if (!pagesToReviewRef.current) {
      scrollToPagesPending.current = true;
      return;
    }
    scrollToPagesPending.current = false;
    pagesToReviewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // A verdict that keeps the section unmounted (no activity in range) drops the
  // pending scroll; otherwise it would fire on a later, unrelated range change.
  useEffect(() => {
    if (!scrollToPagesPending.current) return;
    if (pagesToReviewRef.current) scrollToPagesToReview();
    else if (!noActivityVerdictPending) scrollToPagesPending.current = false;
  });

  // Banner reflects a fixed 7-day assessment, so it is not gated by the selected range.
  const showBanner =
    Boolean(data) && !isDismissed && entitlement.kind !== 'expired-with-data';

  // Gate the impression on the discoverability content actually being visible:
  // not behind the no-integration blur overlay and after the status resolves.
  useEffect(() => {
    if (isNoIntegration || isLoading || !data || tabViewedFiredRef.current) return;
    tabViewedFiredRef.current = true;
    trackEvent('Discoverability: Tab Viewed');
  }, [isNoIntegration, isLoading, data, trackEvent]);

  useEffect(() => {
    if (!data || !showBanner || viewedFiredRef.current) return;
    viewedFiredRef.current = true;
    trackEvent('Discoverability: Status Banner Viewed', {
      status: data.status,
      triggers: data.triggers,
    });
  }, [data, showBanner, trackEvent]);

  const handleDismiss = () => {
    if (!data) return;
    trackEvent('Discoverability: Status Banner Dismissed', {
      status: data.status,
      triggers: data.triggers,
    });
    dismiss();
  };

  const handleGoToDiagnostics = () => {
    if (!data) return;
    trackEvent('Discoverability: Go to Diagnostics Clicked', {
      status: data.status,
      triggers: data.triggers,
    });
    navigate('/ai-insights/diagnostics');
  };

  const handleInspectPages = (tab: DiscoverabilityInspectPagesTab) => {
    // `no_data` synthesizes an empty `evaluatedAt`; the banner that hosts this
    // CTA never renders in that state, but guard defensively so we never build
    // an `Invalid Date` window.
    if (!data || !data.evaluatedAt) return;
    trackEvent('Discoverability: Inspect Pages Clicked', {
      status: data.status,
      triggers: data.triggers,
      tab,
    });
    // Re-anchor the dashboard-wide filter to the exact window the daily scan
    // counted — ending at the snapshot's `evaluatedAt` and spanning the fixed
    // 7-day assessment — so the KPIs, charts and table all align with what the
    // banner reports. The absolute scan-scope carriers (`dateTimeFrom`/
    // `dateTimeTo`, second precision) take precedence over the plain calendar
    // dates, which stay in the URL for the date-picker display and the
    // day-granular KPIs/charts queries.
    const windowEnd = new Date(data.evaluatedAt);
    const windowStart = new Date(windowEnd.getTime() - INSPECT_PAGES_WINDOW_DAYS * MS_PER_DAY);
    const params = new URLSearchParams({
      // Plain calendar dates are local-timezone (matching the picker and the
      // day-granular KPIs/charts); the absolute carriers below stay UTC.
      dateFrom: toLocalCalendarDate(windowStart),
      dateTo: toLocalCalendarDate(windowEnd),
      dateTimeFrom: toSecondPrecisionUtc(windowStart),
      dateTimeTo: toSecondPrecisionUtc(windowEnd),
      tab,
    });
    // Scope the table to exactly the domains the daily scan evaluated. The scan
    // counts only the evaluable (integrated, non-warming-up) domains, while the
    // `/pages/counts` endpoint's no-filter default is every domain with crawl
    // traffic — a wider population — so the evaluable list must always be carried
    // for the tab counts to match what the banner reports. Guard both edges
    // before sending the list: skip an empty list (never write `domains=`, which
    // would widen scope to nothing / 400) and skip a list past the API cap (the
    // endpoints 400 an over-cap list, so fall back to all domains — accepted
    // drift: the table shows every domain rather than error).
    const evaluableDomains = data.domains.filter(isEvaluableDomain).map((d) => d.domain);
    if (evaluableDomains.length > 0 && evaluableDomains.length <= MAX_SCAN_SCOPE_DOMAINS) {
      params.set('domains', evaluableDomains.join(','));
    }
    crawlerSelection.setNoCrawlersSelected(false);
    navigate(`/ai-insights/discoverability?${params.toString()}`);
    scrollToPagesToReview();
  };

  // Deliberately not gated on the charts: the on-screen section only fetches
  // whichever tab is active, so requiring both here would add a second chart
  // request to every page load purely to enable a menu item. The export tab
  // fetches both itself and holds its own print dialog until they land.
  // Not gated on the daily-job status query: it can resolve to `no_data` (or
  // 404-synthesize to it) for an account whose KPIs carry real data, which used
  // to leave the menu item permanently disabled and spinning (PRE-3305).
  const canExportPdf =
    !noCrawlersSelected &&
    !isKpisLoading &&
    !noActivityVerdictPending &&
    !hasNoActivityInRange &&
    hasEverHadCrawlerActivity;

  const handleExportAsPdf = () => {
    trackEvent('Discoverability: Export as PDF Clicked');
    const exportWindow = openExportWindow(
      buildDiscoverabilityExportUrl({ dateFrom, dateTo, crawlers, domain, scanScope })
    );
    if (!exportWindow) {
      message.error('Could not open export tab. Check your browser settings and try again.');
    }
  };

  const handleSelectAllCrawlers = () => pickCrawlers([]);

  const handleStartIntegrationClick = () => {
    trackEvent('Discoverability: Empty State CTA Clicked', {
      cta_type: 'setup_integration',
    });
    navigate('/integration-wizard');
  };

  return (
    <div style={{ position: 'relative' }}>
      <BlurringOverlay
        enabled={isNoIntegration}
        location="AI Insights Discoverability"
        content={NO_INTEGRATION_OVERLAY_CONTENT}
        buttonData={{
          title: 'Start Integration',
          onClick: handleStartIntegrationClick,
        }}
      />
      <Flex gap={token.marginLG} vertical>
        {isLoading || !data ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : showBanner ? (
          <DiscoverabilityStatusBanner
            status={data}
            onDismiss={handleDismiss}
            onGoToDiagnostics={handleGoToDiagnostics}
            onInspectPages={handleInspectPages}
          />
        ) : null}

        <DiscoverabilityFilters
          crawlerSelection={crawlerSelection}
          export={{
            label: EXPORT_AS_PDF_LABEL,
            disabled: !canExportPdf,
            loading: !canExportPdf && !noCrawlersSelected,
            onExportAsPdf: handleExportAsPdf,
          }}
        />

        {noCrawlersSelected ? (
          <NoCrawlersSelected screen="ai_insights_discoverability" onSelectAllCrawlers={handleSelectAllCrawlers} />
        ) : noActivityVerdictPending ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : hasNoActivityInRange ? (
          <DiscoverabilityNoActivityEmptyState />
        ) : (
          <>
            <DiscoverabilityKPIs waitingForCrawlerRequests={waitingForCrawlerRequests} />

            <CrawlerActivityChartsSection
              impressionsReady={!isKpisLoading}
              waitingForCrawlerRequests={waitingForCrawlerRequests}
            />

            <div ref={pagesToReviewRef}>
              <PagesToReviewSection
                waitingForCrawlerRequests={waitingForCrawlerRequests}
                impressionsReady={!isKpisLoading}
              />
            </div>
          </>
        )}
      </Flex>
    </div>
  );
}

type DiscoverabilityViewProps = {
  /** Renders the PDF-export layout instead of the interactive tab. */
  print?: boolean;
};

export default function DiscoverabilityView({ print }: DiscoverabilityViewProps) {
  // Switched before any hook runs, so the two layouts are free to have entirely
  // different data needs — the export fetches both chart series, the screen
  // fetches only the active tab's.
  if (print) return <PrintableDiscoverability />;

  // The export layout brings its own provider; the screen needs one too so the
  // sections can ask whether they are being printed without a missing-provider
  // throw. Mirrors DashboardV3Page and CDNAnalyticsPage.
  return (
    <IsPrintingProvider initialValue={false}>
      <DiscoverabilityScreen />
    </IsPrintingProvider>
  );
}
